import { CommonModule } from '@angular/common';
import {
    AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter,
    inject, Input, OnChanges, Output, signal, SimpleChanges,
} from '@angular/core';

/**
 * Recursive JSON tree renderer with per-node `data-path` attributes — drives
 * the focus-sync between the schema-form (right pane) and the JSON viewer
 * (left pane) on the UserData payload editor.
 *
 * Each rendered line carries `data-path="$.location.city"` etc., and the
 * matching line gets the {@code .jpv-highlighted} class + auto-scroll into
 * view whenever {@link highlightedPath} changes. Replaces the previously-
 * used ngx-json-viewer, which had no public selection API.
 *
 * Self-reference in the template ({@code <app-json-path-viewer>} nested) is
 * resolved by Angular's standalone compiler without needing to import the
 * component into its own {@code imports} array — a component's own
 * selector is always recognised in its own template.
 */
@Component({
    selector: 'app-json-path-viewer',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './json-path-viewer.component.html',
    styleUrl: './json-path-viewer.component.css',
})
export class JsonPathViewerComponent implements OnChanges, AfterViewInit {

    @Input({ required: true }) value: any;

    /** JSON-path prefix this node is rendering. Root call passes "$". */
    @Input() path: string = '$';

    /** Full JSON path to highlight + scroll into view. */
    @Input() highlightedPath: string | null = null;

    /** Emits the JSON path of the line the user clicked. The host uses
     *  this to navigate the column-browser into the selected subtree. */
    @Output() nodeClick = new EventEmitter<string>();

    private el: ElementRef<HTMLElement> = inject(ElementRef);

    /** Paths whose subtree is currently collapsed. Per-instance — each
     *  recursive viewer owns the collapse state of its own direct children. */
    readonly collapsedPaths = signal<Set<string>>(new Set());

    isCollapsed(path: string): boolean {
        return this.collapsedPaths().has(path);
    }

    toggleCollapse(path: string, ev?: Event): void {
        ev?.stopPropagation();
        this.collapsedPaths.update(s => {
            const next = new Set(s);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });
    }

    /** Short summary shown next to a collapsed container — "[3 items]" or
     *  "{4 fields}", matching the ngx-json-viewer convention. */
    containerSummary(v: any): string {
        if (Array.isArray(v)) {
            return v.length === 1 ? '[1 item]' : `[${v.length} items]`;
        }
        if (v && typeof v === 'object') {
            const n = Object.keys(v).length;
            return n === 1 ? '{1 field}' : `{${n} fields}`;
        }
        return '';
    }

    ngAfterViewInit(): void {
        if (this.isRoot && this.highlightedPath) {
            this.scheduleScroll();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Only the root component scrolls; nested copies don't need to act on
        // the input change beyond passing it down to their own children via
        // the template binding.
        if (this.isRoot && changes['highlightedPath'] && this.highlightedPath) {
            this.scheduleScroll();
        }
    }

    private get isRoot(): boolean { return this.path === '$'; }

    /** Defer the scroll-into-view to the next animation frame so Angular's
     *  recursive descendants have had a chance to render their new bindings
     *  (so the highlight class is on the target before we measure). */
    private scheduleScroll(): void {
        requestAnimationFrame(() => this.scrollHighlightIntoView());
    }

    private scrollHighlightIntoView(): void {
        if (!this.highlightedPath) return;
        // JSON paths only contain `$ . [ ] _ a-z A-Z 0-9` — none of those
        // need CSS-escaping inside a quoted attribute value. Keep the
        // selector literal so we don't fight CSS.escape's identifier rules.
        const sel = `[data-path="${this.highlightedPath}"]`;
        const target = this.el.nativeElement.querySelector(sel) as HTMLElement | null;
        if (target) {
            target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }

    // ── Template helpers ────────────────────────────────────────────────────

    isObject(v: any): boolean {
        return v !== null && v !== undefined && typeof v === 'object' && !Array.isArray(v);
    }
    isArray(v: any): boolean { return Array.isArray(v); }
    isLeaf(v: any): boolean {
        return v === null || v === undefined || (typeof v !== 'object');
    }
    leafType(v: any): string {
        if (v === null || v === undefined) return 'null';
        if (typeof v === 'string')  return 'string';
        if (typeof v === 'number')  return 'number';
        if (typeof v === 'boolean') return 'boolean';
        return 'other';
    }
    formatLeaf(v: any): string {
        if (v === null || v === undefined) return 'null';
        if (typeof v === 'string') return `"${v}"`;
        return String(v);
    }
    objectKeys(v: any): string[] { return Object.keys(v ?? {}); }

    pathChild(p: string, k: string): string {
        return p === '$' ? `$.${k}` : `${p}.${k}`;
    }
    pathIndex(p: string, i: number): string { return `${p}[${i}]`; }
}
